import ArrowDownOutlined from '@ant-design/icons/lib/icons/ArrowDownOutlined';
import ArrowUpOutlined from '@ant-design/icons/lib/icons/ArrowUpOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Skeleton from 'antd/lib/skeleton';
import Typography from 'antd/lib/typography';
import { sub } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { ICustomerVen } from 'src/domain/ven/interface';
import { useCustomerVenLogsQuery } from 'src/domain/ven/logs/queries';
import { CustomerVenLogsTimeline } from './CustomerVenLogsTimeline';

interface ICustomerVenLogsModalProps {
    onClose: Function;
    ven: ICustomerVen | Partial<ICustomerVen>;
}

export const CustomerVenLogsModal = ({ onClose, ven }: ICustomerVenLogsModalProps) => {
    const scrollableRef = useRef<HTMLDivElement>(null);
    const { notification } = App.useApp();
    const [datetimeRange, setDatetimeRange] = useState<IDateTimeRange>(getNewerDatetimeRange());

    const {
        data: logs = [],
        isLoading,
        isFetching,
        error,
    } = useCustomerVenLogsQuery(
        { id: ven.id!, ...datetimeRange },
        {
            keepPreviousData: true,
        }
    );

    if (error) {
        notification.error({ key: 'customer-ven-logs-fetch-error', message: error.message || 'Cannot fetch Logs!' });
    }

    useEffect(() => {
        if (scrollableRef.current) {
            // @ts-ignore
            scrollableRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [logs]);

    const handleClose = () => onClose();

    function getNewerLogs() {
        setDatetimeRange(getNewerDatetimeRange());
    }

    function getOlderLogs() {
        setDatetimeRange(getOlderDatetimeRange(datetimeRange));
    }

    return (
        <Modal
            open
            destroyOnHidden
            title={`Logs for Customer VEN "${ven.ven_id}"`}
            onCancel={handleClose}
            width={750}
            styles={{
                body: { maxHeight: '800px', overflow: 'auto' },
            }}
            footer={null}
            className="customer-ven-logs-modal"
        >
            <div ref={scrollableRef}>
                {isLoading && logs.length === 0 ? (
                    <Skeleton loading active />
                ) : (
                    <div>
                        <Button
                            block
                            loading={isFetching}
                            disabled={isFetching}
                            key="get-newer-logs"
                            icon={<ArrowUpOutlined />}
                            onClick={getNewerLogs}
                        >
                            Get Recent Logs
                        </Button>

                        <Typography.Text key="logs-hint" type="secondary">
                            * date and time in your computer timezone, most recent logs on the top
                        </Typography.Text>

                        <CustomerVenLogsTimeline logs={logs} />

                        <Button
                            block
                            loading={isFetching}
                            disabled={isFetching}
                            key="get-older-logs"
                            icon={<ArrowDownOutlined />}
                            onClick={getOlderLogs}
                        >
                            Get Older Logs
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

interface IDateTimeRange {
    start: Date;
    end: Date;
}

function getNewerDatetimeRange() {
    const now = new Date();
    return {
        start: sub(now, { minutes: 60 }),
        end: now,
    };
}

function getOlderDatetimeRange(datetimeRange: IDateTimeRange) {
    return {
        start: sub(datetimeRange.start, { minutes: 60 }),
        end: datetimeRange.start,
    };
}
